import { boot } from 'quasar/wrappers'
import { LocalStorage } from 'quasar'
import { initializeApp } from 'firebase/app'
import { getMessaging, getToken, deleteToken, onMessage, isSupported } from 'firebase/messaging'

/*
 * Web Push (FCM) — Fase 3.
 * Registra o token de push do NAVEGADOR do atendente no backend live, para
 * receber notificação de nova conversa/mensagem mesmo com a aba em segundo
 * plano/congelada. Ver public/firebase-messaging-sw.js e a rota
 * POST /attendants/push-token.
 */
const firebaseConfig = {
  apiKey: process.env.VUE_APP_FCM_API_KEY,
  authDomain: process.env.VUE_APP_FCM_AUTH_DOMAIN,
  projectId: process.env.VUE_APP_FCM_PROJECT_ID,
  messagingSenderId: process.env.VUE_APP_FCM_SENDER_ID,
  appId: process.env.VUE_APP_FCM_APP_ID
}
const VAPID_KEY = process.env.VUE_APP_FCM_VAPID_KEY

let messaging = null
let swRegistration = null
let currentToken = null
let initTried = false

function isConfigured () {
  return !!(firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.messagingSenderId && VAPID_KEY)
}

async function ensureMessaging () {
  if (messaging) return messaging
  if (initTried) return messaging
  initTried = true
  try {
    if (!('serviceWorker' in navigator) || !('Notification' in window)) return null
    if (!(await isSupported().catch(() => false))) return null
    if (!isConfigured()) {
      console.warn('[FCM] chaves VUE_APP_FCM_* não configuradas — push web desabilitado')
      return null
    }
    const appFb = initializeApp(firebaseConfig)
    messaging = getMessaging(appFb)
    // A config do SW é fixa no próprio arquivo (SW não lê process.env) — manter
    // em sincronia com quasar.conf.js / build.env.
    await navigator.serviceWorker.register('/firebase-messaging-sw.js')
    // getToken/pushManager.subscribe exige um SW ATIVO — aguardamos o SW ficar
    // pronto antes de pedir o token (senão: "no active Service Worker").
    swRegistration = await navigator.serviceWorker.ready
    // Foreground = no-op: em foco, o alerta já vem pelo socket (Toast + som) no
    // live.vue. O SW cobre só o background → evita notificação duplicada.
    onMessage(messaging, () => {})
    return messaging
  } catch (e) {
    console.error('[FCM] init error:', e)
    return null
  }
}

async function registerWebPush ($API) {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
    if (!$API || !$API.live) return
    const m = await ensureMessaging()
    if (!m || !swRegistration) return
    const token = await getToken(m, { vapidKey: VAPID_KEY, serviceWorkerRegistration: swRegistration })
    if (!token) return
    currentToken = token
    await $API.live.post('/attendants/push-token', { payload: { token } })
  } catch (e) {
    console.error('[FCM] registerWebPush error:', e)
  }
}

async function unregisterWebPush ($API) {
  try {
    const token = currentToken
    if (messaging) {
      try { await deleteToken(messaging) } catch (e) { /* ignore */ }
    }
    if (token && $API && $API.live) {
      await $API.live.post('/attendants/push-token/remove', { payload: { token } })
    }
    currentToken = null
  } catch (e) {
    console.error('[FCM] unregisterWebPush error:', e)
  }
}

export default boot(({ app }) => {
  // Resolve $API no momento da chamada (o boot de axios já rodou antes deste).
  app.config.globalProperties.$fcm = {
    register: () => registerWebPush(app.config.globalProperties.$API),
    unregister: () => unregisterWebPush(app.config.globalProperties.$API)
  }

  // "Mantenha-me conectado" (checkbox do login → flag 'remember' no LocalStorage):
  //  - marcado    → o token persiste; removido só no logout → notifica mesmo com o navegador fechado.
  //  - desmarcado → ao FECHAR o navegador, remove o token → não notifica depois de fechado.
  // best-effort: pagehide + fetch keepalive (o pagehide pode não disparar se o
  // navegador for encerrado abruptamente).
  window.addEventListener('pagehide', (event) => {
    if (event.persisted) return // vai para bfcache (pode voltar) — não remove
    if (!currentToken) return
    if (LocalStorage.getItem('remember') === true) return // manter conectado → mantém o token
    try {
      const base = process.env.VUE_APP_API_LIVE_DEV || ''
      const $API = app.config.globalProperties.$API
      const auth = $API && $API.live && $API.live.defaults.headers.common.Authorization
      fetch(base + '/attendants/push-token/remove', {
        method: 'POST',
        headers: Object.assign({ 'Content-Type': 'application/json' }, auth ? { Authorization: auth } : {}),
        body: JSON.stringify({ payload: { token: currentToken } }),
        keepalive: true
      })
    } catch (e) { /* best-effort */ }
  })
})
